import { useState } from 'react';
import { Code2, Copy, Check } from 'lucide-react';
import { useAuthStore } from '../../../stores/authStore';
import { useBookingPageSettings } from '../../../hooks/useBookingPageSettings';
import { palette, admin, transition, fontFamily } from '../../../styles/tokens';
import { SettingsSubPage } from '../components/SettingsSubPage';

export function EmbedWidgetPage() {
  const authBusiness = useAuthStore(s => s.business);
  const { config } = useBookingPageSettings(authBusiness?.id);
  const [copied, setCopied] = useState(false);

  const slug = config?.slug ?? authBusiness?.slug ?? '';
  const embedUrl = `https://app.crewbit.ca/book/${slug}`;
  const embedCode = `<iframe\n  src="${embedUrl}"\n  style="width:100%;min-height:700px;border:none;border-radius:12px;"\n  title="Book with ${authBusiness?.name ?? 'us'}"\n  loading="lazy"\n></iframe>\n<script>\n  window.addEventListener('message', function(e) {\n    if (e.data?.type === 'crewbit-resize' && e.data.height) {\n      document.querySelector('iframe[src*="crewbit"]').style.height = e.data.height + 'px';\n    }\n  });\n</script>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* silent */ }
  };

  return (
    <SettingsSubPage title="Embed widget" description="Add booking to your website">
      {/* Preview card */}
      <div style={{
        background: palette.white,
        borderRadius: admin.cardRadius,
        border: `1px solid ${admin.border}`,
        overflow: 'hidden',
        marginBottom: 20,
      }}>
        <div style={{
          padding: '14px 20px',
          borderBottom: `1px solid ${palette.g100}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Code2 size={16} color={palette.g500} strokeWidth={1.8} />
            <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Embed code</span>
          </div>
          <button
            onClick={handleCopy}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 6,
              border: `1px solid ${admin.border}`,
              background: palette.white, fontSize: 12, fontWeight: 500,
              color: copied ? palette.green : palette.g600,
              cursor: 'pointer', fontFamily,
              transition: `all ${transition.fast}`,
            }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy code'}
          </button>
        </div>
        <pre style={{
          padding: '16px 20px',
          margin: 0,
          fontSize: 12,
          lineHeight: 1.6,
          color: palette.g700,
          background: palette.g50,
          overflow: 'auto',
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        }}>
          {embedCode}
        </pre>
      </div>

      {/* Instructions */}
      <div style={{
        background: palette.white,
        borderRadius: admin.cardRadius,
        border: `1px solid ${admin.border}`,
        padding: '16px 20px',
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800, marginBottom: 8 }}>
          How to use
        </div>
        <div style={{ fontSize: 13, color: palette.g500, lineHeight: 1.6 }}>
          1. Copy the embed code above.<br />
          2. Paste it into your website's HTML where you want the booking widget to appear.<br />
          3. The widget auto-resizes to fit your content.<br />
          4. Customers can book directly from your website without leaving the page.
        </div>
      </div>
    </SettingsSubPage>
  );
}
